import React, { useState } from "react";
import {
  Linking,
  StyleSheet,
  Text,
  TextInput,
  useWindowDimensions,
  View,
} from "react-native";
import Clipboard from "@react-native-clipboard/clipboard";

import {
  NormalText,
  PrimaryButton,
  SecondaryButton,
} from "../../../common/native";
import { ADMIN_TOKENS } from "../../../../styles/adminTokens";
import { getLeadCaptureFormBaseUrl } from "../../../../utils/leadCaptureLinks";
import type { TSession } from "../../../../utils/types";
import { useLeadCaptureShareLink } from "./useLeadCaptureShareLink";

type LeadCaptureShareLinkSectionProps = {
  clinicSdkKey?: string;
  deployMode: "widget" | "iframe";
  formName?: string;
  session?: TSession | null;
};

const SOCIAL_DESTINATIONS = [
  "Instagram bio",
  "TikTok bio",
  "Facebook",
  "Ads",
  "DMs",
];

const LeadCaptureShareLinkSection: React.FC<
  LeadCaptureShareLinkSectionProps
> = ({ clinicSdkKey, deployMode, formName, session }) => {
  const { width: viewportWidth } = useWindowDimensions();
  const [isEditingSlug, setIsEditingSlug] = useState<boolean>(false);
  const [isSlugFocused, setIsSlugFocused] = useState<boolean>(false);
  const [isShareLinkCopied, setIsShareLinkCopied] = useState<boolean>(false);
  const [linkActionError, setLinkActionError] = useState<string>("");
  const {
    clearSlugFeedback,
    isLeadCaptureLinkLoading,
    isLeadCaptureLinkSaving,
    isSlugDirty,
    isShareLinkReady,
    normalizeSlugInput,
    resetSlugInput,
    saveSlug,
    setSlugInput,
    shareLink,
    slugError,
    slugInput,
    slugMessage,
  } = useLeadCaptureShareLink({ clinicSdkKey, session });

  const isCustomSlugSupported =
    formName === "onboarding-tier1" || (!formName && deployMode === "widget");
  const isCompactSlugEditor = viewportWidth < 560;

  const handleCopyShareLink = () => {
    if (!isShareLinkReady) return;
    setLinkActionError("");
    Clipboard.setString(shareLink);
    setIsShareLinkCopied(true);
    setTimeout(() => setIsShareLinkCopied(false), 1500);
  };

  const handleOpenShareLink = async () => {
    if (!isShareLinkReady) return;
    setLinkActionError("");
    try {
      await Linking.openURL(shareLink);
    } catch {
      setLinkActionError("Unable to open the form. Copy the link instead.");
    }
  };

  const handleSaveSlug = async () => {
    const nextShareLink = await saveSlug();
    if (nextShareLink) setIsEditingSlug(false);
    setIsShareLinkCopied(false);
  };

  const handleCancelEdit = () => {
    resetSlugInput();
    setIsEditingSlug(false);
    setIsSlugFocused(false);
    setLinkActionError("");
  };

  const handleStartEdit = () => {
    setIsEditingSlug(true);
    setIsShareLinkCopied(false);
    setLinkActionError("");
  };

  if (!isCustomSlugSupported) return null;

  return (
    <View style={styles.shareLinkSection}>
      <View style={styles.sectionHeader}>
        <NormalText
          text="Share Your Lead Capture Form"
          fontWeight="700"
          style={styles.sectionTitle}
        />
        <Text style={styles.sectionDescription}>
          Use this link anywhere patients discover you online. Share it in
          social bios, posts, ads, or direct messages.
        </Text>
      </View>

      <View style={styles.slugEditor}>
        <NormalText
          text="Lead capture link"
          fontWeight="600"
          style={styles.slugLabel}
        />
        <View
          style={[
            styles.slugInputRow,
            isCompactSlugEditor && styles.slugInputRowCompact,
            !isEditingSlug && styles.slugInputRowReadonly,
            isSlugFocused && styles.slugInputRowFocused,
            !!slugError && styles.slugInputRowError,
          ]}
        >
          <Text
            numberOfLines={1}
            style={[
              styles.slugPrefix,
              isCompactSlugEditor && styles.slugPrefixCompact,
            ]}
          >
            {getLeadCaptureFormBaseUrl()}/
          </Text>
          <TextInput
            accessibilityLabel="Custom lead capture URL slug"
            autoCapitalize="none"
            autoCorrect={false}
            editable={isEditingSlug && !isLeadCaptureLinkSaving}
            maxLength={60}
            onBlur={() => {
              setIsSlugFocused(false);
              if (isEditingSlug) normalizeSlugInput();
            }}
            onChangeText={(value) => {
              setSlugInput(value);
              clearSlugFeedback();
            }}
            onFocus={() => setIsSlugFocused(true)}
            placeholder="your-clinic-name"
            placeholderTextColor={ADMIN_TOKENS.color.textMuted}
            returnKeyType="done"
            style={[
              styles.slugInput,
              !isEditingSlug && styles.slugInputReadonly,
              isCompactSlugEditor && styles.slugInputCompact,
            ]}
            value={slugInput}
          />
        </View>
        <View style={styles.primaryActionsRow}>
          {isEditingSlug ? (
            <>
              <PrimaryButton
                text="Save Changes"
                loading={isLeadCaptureLinkSaving}
                disabled={isLeadCaptureLinkLoading || !isSlugDirty}
                onPress={handleSaveSlug}
              />
              <SecondaryButton
                text="Cancel"
                disabled={isLeadCaptureLinkSaving}
                onPress={handleCancelEdit}
              />
            </>
          ) : (
            <>
              <PrimaryButton
                text={isShareLinkCopied ? "Copied" : "Copy Link"}
                disabled={!isShareLinkReady || isLeadCaptureLinkLoading}
                onPress={handleCopyShareLink}
              />
              <SecondaryButton
                text="Open Link"
                disabled={!isShareLinkReady || isLeadCaptureLinkLoading}
                onPress={handleOpenShareLink}
              />
              <SecondaryButton
                text="Edit"
                loading={isLeadCaptureLinkSaving}
                disabled={isLeadCaptureLinkLoading}
                onPress={handleStartEdit}
              />
            </>
          )}
        </View>
        {isLeadCaptureLinkLoading ? (
          <Text style={styles.slugHelperText}>Loading custom slug...</Text>
        ) : slugError ? (
          <Text accessibilityLiveRegion="polite" style={styles.slugErrorText}>
            {slugError}
          </Text>
        ) : slugMessage ? (
          <Text accessibilityLiveRegion="polite" style={styles.slugSuccessText}>
            {slugMessage}
          </Text>
        ) : linkActionError ? (
          <Text accessibilityLiveRegion="polite" style={styles.slugErrorText}>
            {linkActionError}
          </Text>
        ) : (
          <Text style={styles.slugHelperText}>
            {isEditingSlug
              ? "Use 3-60 lowercase letters, numbers, and hyphens."
              : "Click Edit to customize your share URL."}
          </Text>
        )}
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  shareLinkSection: {
    width: "100%",
    maxWidth: ADMIN_TOKENS.size.contentMaxWidth,
    gap: ADMIN_TOKENS.spacing.md,
    padding: ADMIN_TOKENS.spacing.xl,
    borderWidth: ADMIN_TOKENS.borderWidth.sm,
    borderColor: ADMIN_TOKENS.color.borderAccent,
    borderRadius: ADMIN_TOKENS.radius.xl,
    backgroundColor: ADMIN_TOKENS.color.surfaceAccent,
  },
  sectionHeader: {
    gap: ADMIN_TOKENS.spacing.xs,
  },
  sectionTitle: {
    fontSize: ADMIN_TOKENS.fontSize.xl,
    lineHeight: ADMIN_TOKENS.lineHeight.xl,
    color: ADMIN_TOKENS.color.textPrimary,
  },
  sectionDescription: {
    fontFamily: ADMIN_TOKENS.font.medium,
    fontSize: ADMIN_TOKENS.fontSize.md,
    lineHeight: ADMIN_TOKENS.lineHeight.md,
    color: ADMIN_TOKENS.color.textSecondary,
  },
  primaryActionsRow: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: ADMIN_TOKENS.spacing.sm,
  },
  socialDestinationsText: {
    fontFamily: ADMIN_TOKENS.font.medium,
    fontSize: ADMIN_TOKENS.fontSize.sm,
    lineHeight: ADMIN_TOKENS.lineHeight.sm,
    color: ADMIN_TOKENS.color.textSecondary,
  },
  slugEditor: {
    width: "100%",
    gap: ADMIN_TOKENS.spacing.sm,
  },
  slugLabel: {
    fontSize: ADMIN_TOKENS.fontSize.sm,
    lineHeight: ADMIN_TOKENS.lineHeight.sm,
    color: ADMIN_TOKENS.color.textPrimary,
  },
  slugInputRow: {
    minHeight: ADMIN_TOKENS.size.inputHeight,
    borderWidth: ADMIN_TOKENS.borderWidth.sm,
    borderColor: ADMIN_TOKENS.color.borderInput,
    borderRadius: ADMIN_TOKENS.radius.lg,
    flexDirection: "row",
    alignItems: "center",
    overflow: "hidden",
    backgroundColor: ADMIN_TOKENS.color.surface,
  },
  slugInputRowReadonly: {
    borderColor: ADMIN_TOKENS.color.borderAccent,
  },
  slugInputRowCompact: {
    alignItems: "stretch",
    flexDirection: "column",
  },
  slugInputRowFocused: {
    borderWidth: 2,
    borderColor: ADMIN_TOKENS.color.accent,
  },
  slugInputRowError: {
    borderWidth: 2,
    borderColor: ADMIN_TOKENS.color.errorText,
  },
  slugPrefix: {
    flexShrink: 0,
    paddingLeft: ADMIN_TOKENS.spacing.lg,
    paddingRight: ADMIN_TOKENS.spacing.xs,
    fontFamily: ADMIN_TOKENS.font.medium,
    fontSize: ADMIN_TOKENS.fontSize.sm,
    lineHeight: ADMIN_TOKENS.lineHeight.sm,
    color: ADMIN_TOKENS.color.textSecondary,
  },
  slugPrefixCompact: {
    paddingTop: ADMIN_TOKENS.spacing.md,
    paddingRight: ADMIN_TOKENS.spacing.lg,
  },
  slugInput: {
    flex: 1,
    minHeight: ADMIN_TOKENS.size.inputHeight,
    minWidth: 120,
    paddingVertical: ADMIN_TOKENS.spacing.md,
    paddingLeft: 0,
    paddingRight: ADMIN_TOKENS.spacing.lg,
    borderWidth: 0,
    fontFamily: ADMIN_TOKENS.font.medium,
    fontSize: ADMIN_TOKENS.fontSize.md,
    lineHeight: ADMIN_TOKENS.lineHeight.md,
    color: ADMIN_TOKENS.color.textPrimary,
  },
  slugInputReadonly: {
    color: ADMIN_TOKENS.color.textSecondary,
  },
  slugInputCompact: {
    paddingLeft: ADMIN_TOKENS.spacing.lg,
    paddingTop: ADMIN_TOKENS.spacing.xs,
  },
  slugHelperText: {
    fontFamily: ADMIN_TOKENS.font.medium,
    fontSize: ADMIN_TOKENS.fontSize.xs,
    lineHeight: ADMIN_TOKENS.lineHeight.xs,
    color: ADMIN_TOKENS.color.textMuted,
  },
  slugErrorText: {
    fontFamily: ADMIN_TOKENS.font.semibold,
    fontSize: ADMIN_TOKENS.fontSize.xs,
    lineHeight: ADMIN_TOKENS.lineHeight.xs,
    color: ADMIN_TOKENS.color.errorText,
  },
  slugSuccessText: {
    fontFamily: ADMIN_TOKENS.font.semibold,
    fontSize: ADMIN_TOKENS.fontSize.xs,
    lineHeight: ADMIN_TOKENS.lineHeight.xs,
    color: ADMIN_TOKENS.color.accentDark,
  },
});

export default LeadCaptureShareLinkSection;
